import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FeedbackForm.css';
import { getCurrentConfig } from '../config/feedback';

interface FeedbackFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const FeedbackForm: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FeedbackFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showForm, setShowForm] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const config = getCurrentConfig();

      if (config.type === 'development') {
        // Development mode: Show configuration prompt
        alert('Development Mode: Please configure Formspree to enable email sending\n\nSetup Steps:\n1. Visit https://formspree.io\n2. Create a new form\n3. Update the form ID in src/config/feedback.ts\n\nCurrent data:\n' + JSON.stringify(formData, null, 2));
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        return;
      }

      // Formspree submission
      if (config.type === 'formspree') {
        const response = await fetch(config.endpoint!, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            _subject: `CodePlato Feedback: ${formData.subject}`,
            _replyto: formData.email,
          }),
        });

        if (response.ok) {
          setSubmitStatus('success');
          setFormData({ name: '', email: '', subject: '', message: '' });
          setTimeout(() => {
            setShowForm(false);
            setSubmitStatus('idle');
          }, 3000);
        } else {
          setSubmitStatus('error');
        }
      }

    } catch (error) {
      console.error('Feedback submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showForm) {
    return (
      <div className="feedback-trigger">
        <button
          className="feedback-button"
          onClick={() => setShowForm(true)}
          title={t('feedback.button')}
        >
          💬 {t('feedback.button')}
        </button>
      </div>
    );
  }

  return (
    <div className="feedback-overlay">
      <div className="feedback-modal">
        <div className="feedback-header">
          <h3>{t('feedback.title')}</h3>
          <button
            className="close-button"
            onClick={() => setShowForm(false)}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="feedback-form">
          <div className="form-group">
            <label htmlFor="name">{t('feedback.name')}{t('feedback.required')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder={t('feedback.namePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('feedback.email')}{t('feedback.required')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder={t('feedback.emailPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">{t('feedback.subject')}{t('feedback.required')}</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              placeholder={t('feedback.subjectPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">{t('feedback.message')}{t('feedback.required')}</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows={5}
              placeholder={t('feedback.messagePlaceholder')}
            />
          </div>

          {submitStatus === 'success' && (
            <div className="success-message">
              ✅ {t('feedback.successMessage')}
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="error-message">
              ❌ {t('feedback.errorMessage')}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => setShowForm(false)}
            >
              {t('feedback.cancel')}
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;